"use client";

import { useState } from "react";
import {
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalHeader,
  ModalTitle,
  Modal,
  ModalTrigger,
} from "@/components/modal";
import {
  InputOTP,
  InputOTPGroup,
  InputOTPSlot,
} from "@/components/ui/input-otp";
import { Button, buttonVariants } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import { env } from "@/env";
import * as z from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm, Controller } from "react-hook-form";
import { useAuth } from "@/context/AuthContext";

const otpSchema = z.object({
  otp: z.string().regex(/^\d{6}$/, "OTP must be a 6-digit number"),
});

export default function VerifyEmailModal() {
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(1);
  const { toast } = useToast();
  const { accessToken, updateUserDetails } = useAuth();

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<z.infer<typeof otpSchema>>({
    resolver: zodResolver(otpSchema),
  });

  async function resendEmailVerification() {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/resend-email-verification`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
        },
      ).then((res) => res.json());

      if (response.message !== "OK") {
        return toast({
          title: "Error",
          description: response.message,
          variant: "destructive",
        });
      }
      toast({
        title: "Email Sent",
        description: "Verification email has been sent.",
      });

      setStep(2);
    } catch (error) {
      console.error(error);

      toast({
        title: "Error",
        description: "Failed to send verification email.",
        variant: "destructive",
      });
    }
  }

  async function verifyEmail(data: { otp: string }) {
    try {
      const response = await fetch(
        `${env.NEXT_PUBLIC_API_URL}/user/verify-email`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify({ code: Number(data.otp) }),
        },
      ).then((res) => res.json());

      if (response.message !== "OK") {
        return toast({
          title: "Error",
          description: response.message,
          variant: "destructive",
        });
      }
      toast({
        title: "Email Verified",
        description: "Your email has been successfully verified.",
      });

      await updateUserDetails({ verified: true });
      setOpen(false);
    } catch (error) {
      console.error(error);

      toast({
        title: "Error",
        description: "Failed to verify email.",
        variant: "destructive",
      });
    }
  }

  return (
    <Modal open={open} onOpenChange={setOpen}>
      <ModalTrigger className={buttonVariants({ variant: "secondary" })}>
        Verify Email
      </ModalTrigger>
      <ModalContent className="max-w-[17rem]">
        <ModalHeader>
          <ModalTitle>Verify Email</ModalTitle>
          <ModalDescription>
            {step === 1
              ? "Resend verification email"
              : "Enter the code you received"}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          {step === 1 ? (
            <Button onClick={resendEmailVerification}>
              Resend Verification Email
            </Button>
          ) : (
            <form
              onSubmit={handleSubmit(verifyEmail)}
              className="flex flex-col items-center space-y-4"
            >
              <div className="flex flex-col items-start space-y-2">
                <Controller
                  control={control}
                  name="otp"
                  render={({ field }) => (
                    <InputOTP
                      maxLength={6}
                      value={field.value}
                      onChange={field.onChange}
                    >
                      <InputOTPGroup>
                        <InputOTPSlot index={0} />
                        <InputOTPSlot index={1} />
                        <InputOTPSlot index={2} />
                        <InputOTPSlot index={3} />
                        <InputOTPSlot index={4} />
                        <InputOTPSlot index={5} />
                      </InputOTPGroup>
                    </InputOTP>
                  )}
                />
                {errors.otp && (
                  <p className="text-sm text-destructive">
                    {errors.otp.message}
                  </p>
                )}
              </div>
              <Button type="submit" className="mt-4">
                Verify Email
              </Button>
            </form>
          )}
        </ModalBody>
      </ModalContent>
    </Modal>
  );
}
